import { Chart } from 'react-chartjs-2';

import {
  AnimationEvent,
  BarController,
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  PointElement,
  ScriptableScaleContext,
  Title,
} from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { chartValueFormatter, formatUnit } from 'PDF/helpers/chartFormatters';
import { getArrayOfColors, getColorStylesByRecord, getSecondArrayOfColors } from 'PDF/helpers/documentStyle';
import { checkIsEmptyDataByKey } from 'PDF/helpers/pdfMapping';

import { calculatePadding, splitLabel } from 'helpers/charts';
import { formatNumber } from 'helpers/numbers';

import { emptyChart, IChartWithStackedBars } from '../types';
import { listOfLightBackgrounds } from './backgrounds';

ChartJS.register(LinearScale, CategoryScale, BarElement, PointElement, Legend, BarController, Title);

export type IChartWithStackedBarsProps = {
  handleOnComplete: (uri: string) => void;
  info: IChartWithStackedBars;
  aspectRatio?: number;
  isSmall?: boolean;
  withBarTopLabels?: boolean;
  withBarInsideLabels?: boolean;
  withLegend?: boolean;
  axiosInPercentageProps?: {
    min: number;
    max: number;
    stepSize: number;
  };
  styles?: {
    legendAlignment?: 'center' | 'end' | 'start';
    barPercentage?: number;
  };
};

ChartJS.defaults.font.family = 'Noto Sans';

const labelMainProperties = {
  font: {
    size: 26,
    weight: 300,
  },
  color: '#77787C',
};

const oneDatasetColorIndexes = {
  green: [1, 7],
  blue: [1, 0],
  red: [1, 0],
  blueGreen: [1, 0],
  blueRed: [1, 18],
  blueYellow: [1, 8],
};

export const ChartWithStackedBars = ({
  info,
  aspectRatio = 2,
  isSmall = false,
  withBarTopLabels = false,
  withBarInsideLabels = false,
  withLegend = true,
  handleOnComplete,
  axiosInPercentageProps,
  styles = {
    legendAlignment: 'center',
    barPercentage: 1,
  },
}: IChartWithStackedBarsProps) => {
  const { labels, datasets, unit, totals, colors } = info;
  const { barPercentage, legendAlignment } = styles;

  const allValues = [...datasets.flatMap((dataset) => dataset.data.map((value) => Math.ceil(value).toString()))];

  const isOnlyOneDataset = datasets.length === 1;

  const arrayOfColors = getArrayOfColors();
  const secondArrayOfColors = getSecondArrayOfColors();
  const [topLabelColor, oneDatasetColor] = getColorStylesByRecord(oneDatasetColorIndexes);
  const chartData = {
    labels,
    datasets: datasets.map(({ label, data }, index) => ({
      type: 'bar',
      label: index === 0 && isSmall ? label + ' '.repeat(200) : label,
      backgroundColor: isOnlyOneDataset
        ? oneDatasetColor
        : colors
          ? colors[index]
          : datasets.length > 7
            ? secondArrayOfColors[index]
            : arrayOfColors[index],
      data,
      borderWidth: 0,
      barPercentage: barPercentage,
      categoryPercentage: isSmall ? 0.7 : labels.length < 4 ? 0.3 : 0.5,
    })),
  };
  const plugins = [
    ...(unit && !axiosInPercentageProps
      ? [
          {
            id: 'unitPlugin',
            afterDraw(chart: ChartJS) {
              const ctx = chart.ctx;
              const leftX = chart.chartArea?.left;

              ctx.fillStyle = labelMainProperties.color;
              ctx.textAlign = 'right';
              ctx.textBaseline = 'middle';
              ctx.font = '300 24px Noto Sans';

              ctx.fillText(formatUnit(unit), leftX - 18, 20);
            },
          },
        ]
      : []),
    {
      id: 'topLabels',
      afterDatasetsDraw: (chart: ChartJS) => {
        if (!withBarTopLabels) return null;
        const {
          ctx,
          scales: { x },
        } = chart;

        chart.data.datasets[0]?.data.forEach((_, index) => {
          ctx.font = '600 28px Noto Sans';
          ctx.fillStyle = topLabelColor;
          ctx.textAlign = 'center';
          const value = formatNumber(totals[index], 1);

          ctx.fillText(value, x.getPixelForValue(index), chart.getDatasetMeta(datasets.length - 1).data[index]?.y - 30);
        });
      },
    },
  ];
  const options = {
    aspectRatio: isSmall ? 2.3 : aspectRatio,
    devicePixelRatio: 1,
    layout: {
      padding: {
        left: calculatePadding(allValues, unit),
      },
    },
    plugins: {
      legend: {
        display: withLegend,
        position: isSmall ? 'right' : 'bottom',
        align: legendAlignment,
        maxWidth: isSmall ? 800 : undefined,
        labels: {
          boxWidth: 26,
          boxHeight: 26,
          padding: 30,
          ...labelMainProperties,
        },
      },
      title: {
        display: Boolean(unit),
        text: '',
        align: 'start',
        padding: {
          bottom: 30,
          top: 20,
        },
        font: {
          size: 24,
          weight: 400,
        },
        color: labelMainProperties.color,
      },
      datalabels: {
        display: withBarInsideLabels ? 'auto' : false,
        anchor: 'center',
        align: 'center',
        font: {
          size: 26,
          weight: 400,
        },
        formatter: (value: string, context: Context) =>
          chartValueFormatter(value, context, Boolean(axiosInPercentageProps)),
        color: (context: Context) => {
          const backgroundColor = context.dataset.backgroundColor ?? '';
          const colors = ['black', 'white']; // Add more colors as needed
          return listOfLightBackgrounds.includes(backgroundColor as string) ? colors[0] : colors[1];
        },
      },
    },
    scales: {
      y: {
        display: true,
        stacked: true,
        ticks: {
          beginAtZero: true,
          color: '#77787C',
          font: {
            size: 26,
            weight: 300,
          },
          padding: 10,
          ...axiosInPercentageProps,
          callback: axiosInPercentageProps
            ? (value: number) => ((value / axiosInPercentageProps.max) * 100).toFixed(0) + '%'
            : (value: number) => value.toLocaleString(),
        },
        grid: {
          color: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? '#77787C' : 'transparent'),
          tickBorderDash: [3, 7],
        },
        border: { display: false, dash: [3, 7] },
      },
      x: {
        display: true,
        stacked: true,
        ticks: {
          ...labelMainProperties,
          maxRotation: 0,
          callback: function (_: unknown, index: number) {
            return splitLabel(labels[index]?.toString());
          },
        },

        grid: {
          color: 'transparent',
        },
        border: { display: true, color: 'transparent' },
      },
    },
    animation: {
      duration: 0,
      onComplete(event: AnimationEvent) {
        handleOnComplete(checkIsEmptyDataByKey(datasets, 'data') ? emptyChart : event.chart.toBase64Image());
      },
    },
  };

  return (
    <Chart
      type="bar"
      data={chartData as ChartData}
      options={options as ChartOptions}
      plugins={[ChartDataLabels, ...plugins]}
    />
  );
};
